import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {CheckGlyph, KebabGlyph, PencilGlyph, PlusGlyph, TrashGlyph} from '@/components/ui/icons'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import type {Column} from '@/components/data-table'

/** El concepto decide dibujo y tono: dos pantallas no pueden pintar «borrar» distinto. */
export type AccionDeFila = {
  clave: string
  concepto: 'confirmar' | 'editar' | 'borrar' | 'agregar'
  /** ≤ 3 palabras (T2). Si el botón tenía texto, es ese texto: el E2E y el lector lo buscan por nombre (T11). */
  etiqueta: string
  onActivar: () => void
  deshabilitada?: boolean
}

// U18: un dibujo por concepto; el tono sigue al acto (KAI-334).
const DIBUJO = {confirmar: CheckGlyph, editar: PencilGlyph, borrar: TrashGlyph, agregar: PlusGlyph}
const TONO = {
  confirmar: 'text-primary hover:bg-primary/10 hover:text-primary',
  editar: 'text-muted-foreground',
  borrar: 'text-destructive hover:bg-destructive/10 hover:text-destructive',
  agregar: 'text-foreground',
}

/** Ley de Hick: con cuatro o más, dos quedan a la vista y el resto va al menú ⋯ en el tercer lugar. */
const VISIBLES = 3

/** En un teléfono no hay tooltip: la pantalla confirma con un diálogo toda acción que borra; este
 * componente solo llama `onActivar`. */
export function AccionesDeFila({acciones, contexto}: {acciones: readonly AccionDeFila[]; contexto?: string}) {
  const {t} = useTranslation()
  if (acciones.length === 0) return null
  const nombre = (etiqueta: string) =>
    contexto ? t('common.accionesDeFila.nombre', {accion: etiqueta, fila: contexto}) : etiqueta
  const enMenu = acciones.length > VISIBLES
  const aLaVista = enMenu ? acciones.slice(0, VISIBLES - 1) : acciones
  const resto = enMenu ? acciones.slice(VISIBLES - 1) : []
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex justify-end gap-1" data-slot="acciones-de-fila">
        {aLaVista.map((accion) => {
          const Dibujo = DIBUJO[accion.concepto]
          return (
            <ConNombre key={accion.clave} nombre={accion.etiqueta}>
              <Button
                aria-label={nombre(accion.etiqueta)}
                type="button"
                size="icon-sm"
                variant="ghost"
                className={TONO[accion.concepto]}
                disabled={accion.deshabilitada}
                data-testid={`accion-${accion.clave}`}
                onClick={accion.onActivar}>
                <Dibujo />
              </Button>
            </ConNombre>
          )
        })}
        {enMenu && (
          <DropdownMenu>
            <ConNombre nombre={t('common.accionesDeFila.mas')}>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={nombre(t('common.accionesDeFila.mas'))}
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  className="text-muted-foreground">
                  <KebabGlyph />
                </Button>
              </DropdownMenuTrigger>
            </ConNombre>
            <DropdownMenuContent align="end">
              {resto.map((accion) => (
                <DropdownMenuItem
                  key={accion.clave}
                  aria-label={nombre(accion.etiqueta)}
                  disabled={accion.deshabilitada}
                  onSelect={accion.onActivar}>
                  {accion.etiqueta}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </TooltipProvider>
  )
}

function ConNombre({nombre, children}: {nombre: string; children: ReactNode}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{nombre}</TooltipContent>
    </Tooltip>
  )
}

function EncabezadoAcciones(): ReactNode {
  const {t} = useTranslation()
  return <span className="sr-only">{t('common.accionesDeFila.encabezado')}</span>
}

/** `contexto` obligatorio: sin el dato de la fila todas las papeleras se llamarían igual (T11, U21).
 * El encabezado es solo para el lector de pantalla (U20). */
export function columnaDeAcciones<T>({
  acciones,
  contexto,
}: {
  acciones: (fila: T) => readonly AccionDeFila[]
  contexto: (fila: T) => string
}): Column<T> {
  return {
    key: 'acciones',
    header: <EncabezadoAcciones />,
    width: 'w-28',
    className: 'text-right',
    stopClick: true,
    cell: (fila) => <AccionesDeFila acciones={acciones(fila)} contexto={contexto(fila)} />,
  }
}
